<template>
  <section class="gps-ai">
    <header class="gps-ai-head">
      <div>
        <span class="gps-ai-badge"><i class="fa fa-magic"></i> GPS AI</span>
        <h3 class="gps-ai-title">{{ title }}</h3>
        <p v-if="purpose" class="gps-ai-purpose">{{ purpose }}</p>
      </div>
      <b-button
        size="sm"
        class="gps-ai-run"
        :disabled="running || !connected"
        :title="connected ? 'Sends this data to Claude once and saves the answer' : notConnectedText"
        @click="$emit('run')"
      >
        <i class="fa" :class="running ? 'fa-spinner fa-spin' : 'fa-play'"></i>
        {{ running ? 'Analysing…' : (result ? 'Re-run' : runLabel) }}
      </b-button>
    </header>

    <p v-if="loading && !ai" class="gps-ai-muted mb-0">Loading…</p>
    <template v-else>
      <p v-if="!connected" class="gps-ai-warn">
        <i class="fa fa-plug"></i> {{ notConnectedText }}
        <router-link to="/tracking/gps-ai">How to connect</router-link>
      </p>
      <p v-if="ai && ai.error" class="gps-ai-warn"><i class="fa fa-exclamation-circle"></i> {{ ai.error }}</p>

      <div v-if="result" class="gps-ai-result">
        <slot :result="result"></slot>
        <p class="gps-ai-meta">
          Saved {{ time(result.generatedAt) }}<template v-if="result.generatedBy"> by {{ result.generatedBy }}</template>
          · {{ result.model }} · everyone sees this answer until someone re-runs it
        </p>
      </div>
      <p v-else-if="connected && !(ai && ai.error)" class="gps-ai-muted mb-0">
        No GPS AI answer saved yet. Click <strong>{{ runLabel }}</strong> — it runs once and is saved for everyone.
      </p>
    </template>
  </section>
</template>

<script>
import moment from 'moment';

/** Shared look and behaviour for every GPS AI feature: light green, "GPS AI" label, run on click only. */
export default {
  name: 'GpsAiPanel',
  props: {
    title: { type: String, required: true },
    purpose: { type: String, default: '' },
    runLabel: { type: String, default: 'Run GPS AI' },
    /** { status: { connected, reason, model }, result, error } from /api/admin/gps-ai/... */
    ai: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    running: { type: Boolean, default: false }
  },
  computed: {
    status () {
      return (this.ai && this.ai.status) || {};
    },
    connected () {
      return !!this.status.connected;
    },
    result () {
      return this.ai && this.ai.result;
    },
    notConnectedText () {
      return this.status.reason || 'GPS AI is not connected.';
    }
  },
  methods: {
    time (value) {
      return value ? moment(value).format('HH:mm, D MMM YYYY') : '';
    }
  }
};
</script>

<style scoped>
.gps-ai {
  background: #f1faf4;
  border: 1px solid #bfe3cc;
  border-radius: 10px;
  padding: 14px 16px;
}
.gps-ai-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}
.gps-ai-badge {
  display: inline-block;
  background: #3c8969;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 20px;
  padding: 2px 10px;
  margin-bottom: 6px;
}
.gps-ai-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #0e2119;
}
.gps-ai-purpose {
  font-size: 0.82rem;
  color: #4a5a52;
  margin: 2px 0 0;
}
.gps-ai-run {
  background: #3c8969;
  border-color: #3c8969;
  color: #fff;
  white-space: nowrap;
}
.gps-ai-run:hover:not(:disabled) {
  background: #2f6f55;
  border-color: #2f6f55;
}
.gps-ai-warn {
  background: #fff8e6;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.85rem;
  margin: 8px 0 0;
}
.gps-ai-warn a {
  margin-left: 6px;
}
.gps-ai-muted {
  color: #6b7a72;
  font-size: 0.88rem;
}
.gps-ai-result {
  font-size: 0.92rem;
}
.gps-ai-meta {
  font-size: 0.76rem;
  color: #6b7a72;
  margin: 10px 0 0;
}
</style>
